import { Dispatch, SetStateAction } from 'react';
import {
  CalendarRoles,
  isAbleToSeeUIElement,
} from '../../../../utils/roles/client-roles';
import { useQueryClient } from '@tanstack/react-query';
import { UserKeys } from '../../../../api/users-api';
import { Calendar } from '../../../../../types';
import { Messages } from '../../../../utils/message-constants';
import SharedToolbar from '../../../../shared/shared-toolbar-latest';
import SharedGridStateIconButton from '../../../../shared/shared-grid-state-icon-button-latest';
import { ModuleList } from '../../../../shared/analytics/module-list';
import CreateButton from '../../../../shared/buttons/create-button';
import SharedIconButton from '../../../../shared/buttons/shared-icon-button';

interface CalendarGridToolbarProps {
  calendarRoles: string[];
  userEmail?: string;
  setOpenAddCalendar: Dispatch<SetStateAction<boolean>>;
  setOpenDeleteCalendars: Dispatch<SetStateAction<boolean>>;
  setOpenTransferCalendar: Dispatch<SetStateAction<boolean>>;
  setOpenQuickReports: Dispatch<SetStateAction<boolean>>;
  selectedCalendars: Calendar[];
}

export default function CalendarGridToolbar({
  calendarRoles,
  userEmail,
  setOpenAddCalendar,
  setOpenTransferCalendar,
  setOpenDeleteCalendars,
  setOpenQuickReports,
  selectedCalendars,
}: CalendarGridToolbarProps) {
  const queryClient = useQueryClient();

  const isCalendarDisabled = (): boolean => {
    if (userEmail) {
      return !isAbleToSeeUIElement(
        calendarRoles,
        CalendarRoles.CreateCalendars.value
      );
    } else {
      return true;
    }
  };

  return (
    <SharedToolbar
      primaryButtons={
        <CreateButton
          onClick={() => {
            if (userEmail !== '') {
              setOpenAddCalendar(true);
            }
          }}
          disabled={isCalendarDisabled()}
          label="Add Calendar"
        />
      }
      secondaryButtons={
        <>
          <SharedIconButton
            variant="transfer"
            onClick={() => {
              setOpenTransferCalendar(true);
            }}
            disabled={
              !isAbleToSeeUIElement(
                calendarRoles,
                CalendarRoles.CanTransferCalendars.value
              )
            }
            tooltipLabel="Transfer"
          />
          <SharedIconButton
            variant="delete"
            onClick={() => {
              setOpenDeleteCalendars(true);
            }}
            disabled={
              selectedCalendars!.length === 0 ||
              !isAbleToSeeUIElement(
                calendarRoles || [],
                CalendarRoles.DeleteCalendars.value
              )
            }
            tooltipLabel={
              !isAbleToSeeUIElement(
                calendarRoles,
                CalendarRoles.DeleteCalendars.value
              )
                ? Messages.Errors.Permission.Acting(
                    CalendarRoles.DeleteCalendars.name
                  )
                : ''
            }
          />
          <SharedIconButton
            variant="quickReport"
            onClick={() => {
              setOpenQuickReports(true);
            }}
            disabled={!userEmail || userEmail === ''}
            tooltipLabel="Quick Reports"
          />
          <SharedIconButton
            variant="refresh"
            onClick={() => {
              void queryClient.invalidateQueries({
                queryKey: [UserKeys.Calendars, userEmail],
              });
              void queryClient.invalidateQueries({
                queryKey: [UserKeys.CalendarEvents, userEmail],
              });
              void queryClient.invalidateQueries({
                queryKey: [UserKeys.CalendarAcls, userEmail],
              });
            }}
            tooltipLabel="Refresh"
          />
          <SharedGridStateIconButton
            gridStateKey="calendarsGridStates"
            module={ModuleList.modules.userManagement.name}
            section={ModuleList.modules.userManagement.sections.calendars}
          />
        </>
      }
    ></SharedToolbar>
  );
}
